import { useState } from 'react'
import { ArrowRight, Loader2, Merge } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { supabase } from '../lib/supabase'
import { todayISO } from '../lib/dates'
import { guestLabel, guestStats, type DuplicatePair } from '../lib/guests'
import { Button, Sheet, cx } from '../ui/kit'

/**
 * Zwei Karteien zu einer machen.
 *
 * ES IST UNUMKEHRBAR, und deshalb steht hier nicht „Wirklich zusammenführen?",
 * sondern die Aufstellung: welcher Datensatz bleibt, welcher aufgeht, wie viele
 * Buchungen wandern, und was am aufgehenden Datensatz hängen bleibt. Wer das nicht
 * vorher sieht, kann es nachher nur bereuen.
 *
 * WAS DIE DATENBANK TUT (siehe merge_guests() in Migration 0043): Buchungen und
 * Rechnungsentwürfe hängen um; leere Felder des bleibenden Gastes werden aus dem
 * anderen gefüllt, gefüllte NIE überschrieben; Notizen werden aneinandergehängt statt
 * verworfen. Ausgestellte Rechnungen bleiben, wo sie sind — eine Rechnung
 * nachträglich einer anderen Person zuzuordnen wäre eine Belegfälschung. Hat der
 * aufgehende Gast solche Rechnungen, bleibt seine Zeile als entkernter Stummel
 * stehen statt gelöscht zu werden.
 */
export default function GuestMergeDialog({
  pair, open, onClose, onMerged,
}: {
  pair: DuplicatePair | null
  open: boolean
  onClose: () => void
  onMerged: () => void
}) {
  const { lang, formatDate, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  // Welcher der beiden bleibt. Vorbelegt ist der mit mehr Substanz, aber der
  // Vermieter entscheidet: manchmal ist der ältere Datensatz der gepflegtere.
  const [keepFirst, setKeepFirst] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!pair) return null

  const statsOf = (id: string) => guestStats({
    guestId: id,
    bookings: store.db.bookings,
    invoices: store.db.invoices,
    payments: store.db.payments,
    today: todayISO(),
  })

  const keep = keepFirst ? pair.a : pair.b
  const gone = keepFirst ? pair.b : pair.a
  const goneStats = statsOf(gone.id)
  const goneBookings = store.db.bookings.filter((b) => b.guest_id === gone.id).length
  const goneInvoices = store.db.invoices.filter(
    (i) => i.guest_id === gone.id && Boolean(i.issued_at)).length

  const run = async () => {
    if (!store.live || !supabase) return
    const question = de
      ? `„${guestLabel(gone)}" in „${guestLabel(keep)}" zusammenführen?\n\n`
        + `• ${goneBookings} Buchung(en) werden umgehängt\n`
        + `• leere Felder werden ergänzt, gefüllte bleiben\n`
        + `• Notizen und Schlagworte werden zusammengelegt\n`
        + (goneInvoices > 0
          ? `• ${goneInvoices} ausgestellte Rechnung(en) bleiben beim alten Datensatz — er bleibt als entkernter Eintrag bestehen\n`
          : '• der alte Datensatz wird gelöscht\n')
        + '\nDas lässt sich nicht rückgängig machen.'
      : `Merge “${guestLabel(gone)}” into “${guestLabel(keep)}”?\n\n`
        + `${goneBookings} booking(s) will move. This cannot be undone.`
    if (!window.confirm(question)) return

    setBusy(true); setError(null)
    const { error: e } = await supabase.rpc('merge_guests', {
      p_keep: keep.id, p_merge: gone.id,
    })
    if (e) setError(e.message)
    else { onMerged(); store.reload(); onClose() }
    setBusy(false)
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={de ? 'Gäste zusammenführen' : 'Merge guests'}
      footer={
        <>
          <Button className="flex-1" variant="ghost" onClick={onClose}>
            {de ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button
            className="flex-1" variant="danger"
            onClick={() => void run()} disabled={busy || !store.live}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Merge size={16} />}
            {de ? 'Zusammenführen' : 'Merge'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted">
        {de
          ? 'Wählen Sie den Datensatz, der bestehen bleibt. Der andere geht darin auf.'
          : 'Choose the record that stays. The other one is merged into it.'}
      </p>

      <div className="space-y-2">
        {[pair.a, pair.b].map((g, index) => {
          const chosen = (index === 0) === keepFirst
          const s = statsOf(g.id)
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => setKeepFirst(index === 0)}
              className={cx(
                'w-full text-left rounded-2xl border p-3 space-y-1',
                chosen ? 'border-accent bg-accent-soft/40' : 'border-border bg-surface',
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-medium truncate flex-1">{guestLabel(g)}</span>
                <span className="text-xs text-muted shrink-0">
                  {chosen ? (de ? 'bleibt' : 'stays') : (de ? 'geht auf' : 'merged')}
                </span>
              </div>
              <div className="text-xs text-muted space-y-0.5">
                {g.email && <div className="truncate">{g.email}</div>}
                {g.phone && <div>{g.phone}</div>}
                {(g.postcode || g.city) && <div>{[g.postcode, g.city].filter(Boolean).join(' ')}</div>}
                <div className="tabular-nums">
                  {de ? `${s.stays} Aufenthalte · ${formatMoney(s.revenue)}` : `${s.stays} stays · ${formatMoney(s.revenue)}`}
                  {s.lastStay && ` · ${de ? 'zuletzt ' : 'last '}${formatDate(s.lastStay)}`}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      <div className="rounded-xl bg-surface-2 p-3 text-sm space-y-1">
        <div className="flex items-center gap-2 font-medium">
          <span className="truncate">{guestLabel(gone)}</span>
          <ArrowRight size={14} className="shrink-0" />
          <span className="truncate">{guestLabel(keep)}</span>
        </div>
        <ul className="list-disc pl-5 text-muted space-y-0.5">
          <li>
            {de ? `${goneBookings} Buchung(en) werden umgehängt` : `${goneBookings} booking(s) move`}
            {goneStats.stays > 0 && (de
              ? ` — der Umsatz zählt danach beim bleibenden Gast mit`
              : ' — revenue counts on the remaining guest afterwards')}
          </li>
          <li>
            {de
              ? 'Leere Felder werden ergänzt, gefüllte nie überschrieben; Notizen und Schlagworte werden zusammengelegt.'
              : 'Empty fields get filled in, filled ones are never overwritten; notes and tags are combined.'}
          </li>
          <li>
            {goneInvoices > 0
              ? (de
                ? `${goneInvoices} ausgestellte Rechnung(en) bleiben beim alten Datensatz — eine ausgestellte Rechnung darf niemand nachträglich einer anderen Person zuordnen. Der alte Eintrag bleibt deshalb entkernt bestehen und verschwindet aus der Kartei.`
                : `${goneInvoices} issued invoice(s) stay with the old record — an issued invoice may never be reassigned. The old entry remains as a stripped stub and leaves the list.`)
              : (de
                ? 'Der alte Datensatz wird gelöscht — er trägt keine ausgestellte Rechnung.'
                : 'The old record is deleted — it carries no issued invoice.')}
          </li>
        </ul>
      </div>

      {!store.live && (
        <p className="text-sm text-muted rounded-xl bg-surface-2 p-3">
          {de
            ? 'Das Zusammenführen läuft in der Datenbank und steht nur im echten Konto zur Verfügung.'
            : 'Merging runs in the database and is only available in a real account.'}
        </p>
      )}
      {error && <p className="text-sm text-danger">{error}</p>}
    </Sheet>
  )
}
